// White-label theming: ThemeConfig -> CSS variables applied on microsite scopes
// (.brand-scope). Curated font pairings + guarded color slots — no free-form CSS.

import { FONT_PAIRINGS } from "@/lib/fonts";

export const FONT_STACKS = {
  sf: '-apple-system, "SF Pro Display", Inter, sans-serif',
  inter: '"Inter", -apple-system, sans-serif',
  playfair: '"Playfair Display", Georgia, serif',
  cormorant: '"Cormorant Garamond", Georgia, serif',
  manrope: '"Manrope", -apple-system, sans-serif',
};

export function brandStyle(theme) {
  if (!theme) return {};
  const style = {};
  if (theme.primary_color) style["--brand-accent"] = theme.primary_color;
  if (theme.secondary_color) style["--brand-secondary"] = theme.secondary_color;
  if (theme.button_color) style["--brand-button"] = theme.button_color;
  if (theme.link_color) style["--brand-link"] = theme.link_color;
  if (theme.highlight_color) style["--brand-highlight"] = theme.highlight_color;
  const pairing = theme.font_pairing && FONT_PAIRINGS[theme.font_pairing];
  if (pairing) {
    style["--brand-font"] = pairing.body;
    style["--brand-font-heading"] = pairing.heading;
  } else if (theme.font && FONT_STACKS[theme.font]) {
    style["--brand-font"] = FONT_STACKS[theme.font];
    style["--brand-font-heading"] = FONT_STACKS[theme.font];
  }
  return style;
}

/** data-radius attribute value for the brand scope (CSS presets in index.css). */
export function radiusAttr(theme) {
  return theme?.radius || "soft";
}

/* ---------- color guardrails (builder contrast checks) ---------- */

function hexToRgb(hex) {
  const h = hex?.replace("#", "");
  if (!h || (h.length !== 6 && h.length !== 3)) return null;
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
}

function luminance(rgb) {
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colors (1..21). */
export function contrastRatio(hexA, hexB) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return 21;
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}
